/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../../../../context/ToastProvider';
import { useNavigate } from 'react-router-dom';
import EmptyState from './graphs/empty-grphs';

import { Button } from 'primereact/button';
import { ProgressBarComponent } from '../../../../../../components/progress-bar/progress-bar';
import { Role, ROLES } from '@marsinet/commons';
import { apiServices } from '../../../../../../services/apiServices';
import { MarsinetContext } from '../../../../../../context/marsinetProvider';

import {
  getBarDistributionGraph,
  getFCGraphIntra,
  IntrasessionView,
  MotionModeString,
} from '@marsinet/commons';

import { getMotionModeStringLabels } from '@marsinet/web-commons';

import {
  formatDateFromDB,
} from '../../../../../../../../src/app/utils/utils';

import { Menu } from 'primereact/menu';
import type { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem';
import VitalsGraphs from './tabs/vital-graphs';
import AutoGraphs from './tabs/auto-graphs';
import TriggerForceGraphs from './tabs/trigger-force-graphs';
import AanGraphs from './tabs/aan-graphs';
import { TabButton } from './components/button-tabs';
import { DeviceModelType } from 'apps/marsinet/src/app/models/devicesModels';

export interface GraphsIntrasessionProps {
  ingestionId: string;
  zipFileName: string;
  timeWalking: number;
  stepsTotal: number;
  date: string;
  p_id?: string;
  startDate?: Date;
  endDate?: Date;
  deviceModel?: DeviceModelType;
}

type ActiveTab = 'VITALS' | 'AUTO' | 'TRIGGER_FORCE' | 'AAN';

const ALL_TABS: readonly ActiveTab[] = ['VITALS', 'AUTO', 'TRIGGER_FORCE', 'AAN'];

export const VALID_TABS_BY_DEVICE: Record<DeviceModelType, readonly ActiveTab[]> = {
  [DeviceModelType.ATLAS]: ['AUTO', 'TRIGGER_FORCE'],
  [DeviceModelType.EXPLORER_PERSONAL]: ['AUTO', 'TRIGGER_FORCE'],
  [DeviceModelType.EXPLORER_CLINICO]: ALL_TABS,
  [DeviceModelType.ALL_DEVICES]: [],
};

export const getValidTabs = (
  model?: DeviceModelType | null
): readonly ActiveTab[] =>
  model == null ? ALL_TABS : VALID_TABS_BY_DEVICE[model] ?? ALL_TABS;


// Fuera del componente (archivo módulo)
const INITIAL_GRAPH_DATA = {
  vitals: { heartRate: null as any, oxygenSaturation: null as any },
  auto: {
    raw: null as any,
    motorsAng: null as any[],
    distributionHip: null as any,
    distributionKnee: null as any,
  },
  trigger: {
    left: null as any[],
    right: null as any[],
    averageStepTime: null as any,
    rom: null as any[], //--> Nuevo para almacenar los datos de ROM (Range of Motion)
  },
  aan: {
    assistanceLevels: null as any[],
    joint: {
      hipL: null as any[],
      hipR: null as any[],
      kneeL: null as any[],
      kneeR: null as any[],
    },
    assistedTorque: null as any[],
    kinematics: {
      leftHip: null as any,
      rightHip: null as any,
      leftKnee: null as any,
      rightKnee: null as any,
    },
  },
};

export const GraphIntrasessionComponents = (
  props: Readonly<GraphsIntrasessionProps>
) => {
  const navigate = useNavigate();
  const { p_id } = props;

  const { t } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  const { showToast: show } = useToast();

  const ingestionId = props.ingestionId;
  const timeWalking = props.timeWalking;
  const stepsTotal = props.stepsTotal;
  const date = props.date;

  const [isLoading, setIsLoading] = useState(true);
  const [graphData, setGraphData] = useState<any>(INITIAL_GRAPH_DATA);
  const [deviceModel, setDeviceModel] = useState<DeviceModelType | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('AUTO');

  // Brush
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [baseLen, setBaseLen] = useState(0);

  const allowedTabs = useMemo(() => getValidTabs(deviceModel), [deviceModel]);

  const onMenuCommand = useCallback((e: MenuItemCommandEvent) => {
    const id = e.item?.id as ActiveTab | undefined;
    if (id) setActiveTab(id);
  }, []);

  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0]);
    }
  }, [allowedTabs, activeTab]);

  const labels = useMemo(() => getMotionModeStringLabels(t), [t]);


  const makeTemplate = useCallback(
    (id: ActiveTab, icon: string, tooltip: string) =>
      (_item: any, options: any) => (
        <TabButton
          icon={icon}
          tooltip={tooltip}
          active={activeTab === id}
          actionProps={options.actionProps}
          onClick={options.onClick}
        />
      ),
    [activeTab]
  );

  const menuItems: MenuItem[] = useMemo(() => {
    const config: { id: ActiveTab; icon: string; tooltip: string }[] = [

      {
        id: 'AUTO',
        icon: 'pi pi-chart-pie',
        tooltip: t('pages.patients.viewPatient.sessions.general'),
      },
      {
        id: 'TRIGGER_FORCE',
        icon: 'pi pi-sliders-h',
        tooltip:
          deviceModel === DeviceModelType.ATLAS
            ? t('pages.reports.graphs.activeMode')
            : labels[MotionModeString.TRIGGER_FORCE],
      },
      {
        id: 'AAN',
        icon: 'pi pi-chart-line',
        tooltip: labels[MotionModeString.AAN],
      },
      {
        id: 'VITALS',
        icon: 'pi pi-heart',
        tooltip: t('pages.patients.viewPatient.sessions.vitals'),
      },
    ];

    return config
      .filter((item) => allowedTabs.includes(item.id))
      .map(({ id, icon, tooltip }) => ({
        id,
        command: onMenuCommand,
        template: makeTemplate(id, icon, tooltip),
      }));
  }, [allowedTabs, deviceModel, onMenuCommand, makeTemplate, t, labels]);

  const tabToView = useMemo<Record<ActiveTab, IntrasessionView>>(
    () => ({
      VITALS: IntrasessionView.VITALS,
      AUTO: IntrasessionView.AUTO,
      TRIGGER_FORCE: IntrasessionView.TRIGGER_FORCE,
      AAN: IntrasessionView.AAN,
    }),
    []
  );

  useEffect(() => {
    if (!ingestionId) return;
    if (!allowedTabs.includes(activeTab)) return;
    setIsLoading(true);

    const fetchData = async () => {
      try {
        const view = tabToView[activeTab];
        const response: any =
          await apiServices.ingestionApi.ingestionControllerFindOne(
            ingestionId,
            'intrasession',
            view
          );

        const data = response?.data?.csvData;

        if (!data) {
          show('info', t('messages.info'), t('Unexpected response format'));
          return;
        }

        const modelFromApi = response?.data?.metadata?.model as DeviceModelType | undefined;
        if (modelFromApi != null) setDeviceModel(modelFromApi);

        // Setea sólo lo que devuelve cada vista
        switch (activeTab) {
          case 'VITALS': {
            const heartRateProcessed = getFCGraphIntra(
              data?.heartRate,
              t,
              p_id
            );
            const oxygenValues = (data?.oxygenSaturation ?? [])
              .map((it: any) => Number(it?.oxygenSaturation))
              .filter((n: number) => Number.isFinite(n));
            setGraphData((prev) => ({
              ...prev,
              vitals: {
                heartRate: heartRateProcessed,
                oxygenSaturation: oxygenValues.length
                  ? {
                    min: Math.min(...oxygenValues),
                    max: Math.max(...oxygenValues),
                  }
                  : null,
              },
            }));
            break;
          }
          case 'AUTO': {
            const raw = data;
            setGraphData((prev: any) => ({
              ...prev,
              auto: {
                raw,
                motorsAng: data?.motorsAngSeriesData || [],
              },
            }));
            break;
          }
          case 'TRIGGER_FORCE': {
            setGraphData((prev: any) => ({
              ...prev,
              trigger: {
                left: data?.leftJointThresholdAndEffort || [],
                right: data?.rightJointThresholdAndEffort || [],
                rom: data?.rom || [], // ← nuevo para traer el rom dentro de la grafica
                averageStepTime: {
                  averageStepTimeLeftLeg: getBarDistributionGraph(
                    data?.averageStepTime?.leftLegAverageStepTime,
                    t(
                      'pages.patients.viewPatient.sessions.avgStepTimeSeconds'
                    ) +
                    ' ' +
                    t('pages.patients.viewPatient.sessions.leftLeg'),
                    t
                  ),
                  averageStepTimeRightLeg: getBarDistributionGraph(
                    data?.averageStepTime?.rightLegAverageStepTime,
                    t(
                      'pages.patients.viewPatient.sessions.avgStepTimeSeconds'
                    ) +
                    ' ' +
                    t('pages.patients.viewPatient.sessions.rightLeg'),
                    t
                  ),
                },
              },
            }));
            break;
          }
          case 'AAN': {
            setGraphData((prev: any) => ({
              ...prev,
              aan: {
                assistanceLevels: data?.assistanceLevelData || [],
                joint: {
                  hipL: data?.jointPositions?.hipLeft || [],
                  hipR: data?.jointPositions?.hipRight || [],
                  kneeL: data?.jointPositions?.kneeLeft || [],
                  kneeR: data?.jointPositions?.kneeRight || [],
                },
                assistedTorque: data?.assistedTorqueData || [],
                kinematics: {
                  leftHip: data?.ang_hip_l || null,
                  rightHip: data?.ang_hip_r || null,
                  leftKnee: data?.ang_knee_l || null,
                  rightKnee: data?.ang_knee_r || null,
                },
              },
            }));
            break;
          }
        }
      } catch (_error) {
        //console.error('Error fetching ingestion data', _error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [ingestionId, activeTab, allowedTabs, p_id, t, tabToView, show]);

  const MAX_SELECTION_RANGE = 2200;

  useEffect(() => {
    if (baseLen > 0) {
      const initial = Math.floor(baseLen * 0.05);
      setStartIndex(0);
      setEndIndex(Math.min(baseLen - 1, initial));
    } else {
      setStartIndex(0);
      setEndIndex(0);
    }
  }, [baseLen, activeTab]);

  const handleBrushChange = useCallback(
    ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
      const range = endIndex - startIndex;

      if (range > MAX_SELECTION_RANGE) {
        show(
          'warn',
          'Advertencia',
          t('pages.maintenance.ingestion.graphs.outrange')
        );
        setStartIndex(startIndex);
        setEndIndex(startIndex + MAX_SELECTION_RANGE);
      } else {
        setStartIndex(startIndex);
        setEndIndex(endIndex);
      }
    },
    [MAX_SELECTION_RANGE, show, t]
  );

  const hasDataForTab = (data: any, tab: ActiveTab): boolean => {
    switch (tab) {
      case 'VITALS':
        return !!data?.vitals?.heartRate || !!data?.vitals?.oxygenSaturation;
      case 'AUTO':
        return !!data?.auto?.raw || (data?.auto?.motorsAng?.length ?? 0) > 0;
      case 'TRIGGER_FORCE':
        return (
          (data?.trigger?.left?.length ?? 0) > 0 ||
          (data?.trigger?.right?.length ?? 0) > 0 ||
          (data?.trigger?.averageStepTime?.averageStepTimeLeftLeg
            ?.dataBarDistribution?.labels?.length ?? 0) > 0
        );
      case 'AAN':
        return (
          (data?.aan?.assistanceLevels?.length ?? 0) > 0 ||
          (data?.aan?.assistedTorque?.length ?? 0) > 0 ||
          (data?.aan?.joint?.hipL?.length ?? 0) > 0
        );
      default:
        return false;
    }
  };

  if (isLoading) return <ProgressBarComponent />;

  return (
    <div className="col-12 xl:pl-7 xl:pr-7 p-2 sm:p-3 md:p-4">
      <div className="col-12 shadow-3 surface-card border-round-xl mb-4">
        {/* Header */}
        <div className="p-2 flex align-items-center flex-wrap">
          <Button
            icon="pi pi-angle-left"
            rounded
            onClick={() =>
              navigate(`/authenticated/patients/${p_id}`, {
                state: {
                  graphType: 'intrasession',
                  startDate: props.startDate,
                  endDate: props.endDate,
                },
                replace: true,
              })
            }
            severity="secondary"
            className="mr-2 mb-2 sm:mr-4"
            aria-label="Bookmark"
          />
          <div className="mr-2 mb-2">
            <h1 className="text-center text-gray-700 mb-3 mr-auto">
              {t('pages.patients.viewPatient.sessions.sessionDetails')}:{' '}
              {formatDateFromDB(date, selectedTimezone)}
              <i className="pi pi-calendar ml-2" style={{ fontSize: '2rem' }} />
            </h1>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="col-12 shadow-3 surface-card border-round-xl mt-4 p-4 text-gray-700 text-center">
        <div className="grid row-gap-3">
          {allowedTabs.length > 0 && (
            <aside
              className="col-12 lg:col-1 lg:sticky"
              style={{ top: '5rem', maxHeight: 'calc(100vh - 5rem)' }}
            >
              <Menu
                model={menuItems}
                className="w-full border-none surface-transparent"
                pt={{
                  menu: {
                    className:
                      'flex flex-row flex-nowrap lg:flex-column w-full justify-content-center lg:justify-content-start gap-2',
                  },
                  menuitem: { className: 'w-auto lg:w-full' },
                  action: {
                    className:
                      'block w-full p-0 m-0 focus:outline-none focus:ring-0',
                  },
                  label: { className: 'hidden' },
                }}
              />
            </aside>
          )}

          {/* --- Contenido: aquí va el empty state --- */}
          <section
            className={
              allowedTabs.length > 0
                ? 'col-12 lg:col-11 px-2 md:px-4'
                : 'col-12 px-2 md:px-4'
            }
            style={{ minWidth: 0 }}
          >
            {allowedTabs.length === 0 || !hasDataForTab(graphData, activeTab) ? (
              <EmptyState icon="pi pi-info-circle" title={t('emptyMessage')} />
            ) : (
              <>
                {activeTab === 'VITALS' && (
                  <VitalsGraphs
                    heartRate={graphData.vitals.heartRate}
                    oxygenSaturation={graphData.vitals.oxygenSaturation}
                    t={t}
                    setLoading={setIsLoading}
                  />
                )}

                {activeTab === 'AUTO' && (
                  <AutoGraphs
                    t={t}
                    raw={graphData.auto?.raw}
                    motorsAng={graphData.auto?.motorsAng}
                    deviceModel={deviceModel}
                    timeWalking={timeWalking}
                    stepsTotal={stepsTotal}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onBrushChange={handleBrushChange}
                    onBaseLenChange={setBaseLen}
                  />
                )}

                {activeTab === 'TRIGGER_FORCE' && (
                  <TriggerForceGraphs
                    t={t}
                    deviceModel={deviceModel}
                    left={graphData.trigger?.left}
                    right={graphData.trigger?.right}
                    rom={graphData.trigger?.rom}
                    averageStepTime={graphData.trigger?.averageStepTime}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onBrushChange={handleBrushChange}
                    onBaseLenChange={setBaseLen}
                  />
                )}

                {activeTab === 'AAN' && (
                  <AanGraphs
                    t={t}
                    aanData={graphData.aan}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onBrushChange={handleBrushChange}
                    onBaseLenChange={setBaseLen}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};